import './onboarding.less';

import * as Business        from '../business';
import * as User            from '../user';
import { ArrowRight }       from 'react-feather';
import { compose }          from 'redux';
import { connect }          from 'react-redux';
import { useEffect }        from 'react';
import BroadlyIcon          from './broadly_icon.svg';
import BroadlyLogo          from './broadly_logo.svg';
import FetchFromServer      from '../components/fetch_from_server';
import GoogleSection        from './google_place';
import IntegrationSection   from './integration';
import Link                 from '../components/link';
import NotificationsSection from './notifications';
import OnboardingSection    from './onboarding_section';
import PasswordSection      from './password';
import React                from 'react';
import useNavigateTo        from '../hooks/use_navigate_to';
import WebChatSection       from './web_chat';


export default connect(
  (state, { businessID }) => ({
    businessID,
    profile:         User.getProfile(state),
    firstName:       User.getFirstName(state),
    business:        Business.getBusiness(state, businessID),
    locations:       User.getLocations(state),
    onboarding:      Business.getOnboarding(state, businessID),
    needsOnboarding: Business.needsOnboarding(state, businessID),
    googlePlace:     Business.getGooglePlace(state, businessID),
    reviews:         Business.getReviews(state, businessID),
    statuses:        [
      Business.getBusinessStatuses(state, businessID),
      Business.getReviewsStatuses(state, businessID)
    ]
  }),
  (dispatch, { businessID }) => ({
    load:             compose(dispatch, Business.readReviews, () => (businessID)),
    updateProfile:    compose(dispatch, User.updateProfile),
    updateOnboarding: compose(dispatch, Business.updateBusinessOnboarding, onboarding => ({ businessID, onboarding }))
  })
)(OnboardingPage);


function OnboardingPage({
  businessID,
  profile,
  firstName,
  business,
  onboarding,
  googlePlace,
  reviews,
  statuses,
  load,
  updateProfile,
  needsOnboarding,
  updateOnboarding
}) {
  const navigateTo = useNavigateTo();

  useEffect(function() {
    if (needsOnboarding === false)
      navigateTo(`/${businessID}/inbox`, { reloadCurrent: true });
  }, [ businessID, navigateTo, needsOnboarding ]);

  useEffect(() => {
    if (needsOnboarding) {
      document.documentElement?.classList?.add('onboarding');

      return () => {
        document.documentElement?.classList?.remove('onboarding');
      };
    } else
      return undefined;
  }, [ needsOnboarding ]);

  if (!needsOnboarding)
    return null;

  const isReady = statuses.every(({ readStatus }) => readStatus.succeeded);

  return (
    <div className="onboarding-page">
      <IntroSection {...{ profile, firstName }}/>
      <FetchFromServer statuses={statuses} load={load} pullToRefresh={false}>
        {isReady && (
          <OtherSections {...{
            businessID,
            navigateTo,
            profile,
            business,
            onboarding,
            googlePlace,
            reviews,
            updateProfile,
            updateOnboarding
          }}/>
        )}
      </FetchFromServer>
    </div>
  );
}


function IntroSection({ profile, firstName }) {
  const { email } = profile;
  return (
    <OnboardingSection className="intro">
      <div className="intro-header">
        <BroadlyIcon className="broadly-icon"/>
        <BroadlyLogo className="broadly-logo"/>
        {email && (
          <div className="signed-in-as">
            Signed in as<br/>{email}
          </div>
        )}
      </div>
      <h1>Hello {firstName}. &nbsp;👋</h1>
      <p>Thank you for choosing Broadly. We’re glad you’re here and can’t wait to help you attract and retain more customers.</p>
      <p>Let’s get you setup, so you can start providing a great customer experience with Broadly.</p>
    </OnboardingSection>
  );
}

function OtherSections({
  businessID,
  navigateTo,
  profile,
  onboarding,
  googlePlace,
  reviews,
  updateProfile,
  updateOnboarding
}) {

  const { scheduledCallDate } = onboarding;
  const { integrationName }   = onboarding;

  const showGoogleSection      = !!(googlePlace && reviews);
  const showIntegrationSection = !!scheduledCallDate;

  return (
    <>
      {showGoogleSection && <GoogleSection {...{ googlePlace, reviews }}/>}
      <WebChatSection {...{ businessID }}/>
      {showIntegrationSection && <IntegrationSection {...{ scheduledCallDate, integrationName, updateOnboarding }}/>}
      <PasswordSection {...{ profile, updateProfile }}/>
      <NotificationsSection/>
      <OutroSection {...{ businessID, navigateTo, updateOnboarding }}/>
    </>
  );
}


function OutroSection({ businessID, navigateTo, updateOnboarding }) {
  const href = `/${businessID}/inbox`;

  async function setOnboardingStatusToComplete() {
    await updateOnboarding({ status: 'completed' });
    navigateTo(href, { reloadCurrent: true });
  }

  return (
    <OnboardingSection className="outro">
      <h2>That’s all for now! &nbsp;🥳</h2>
      <p>
        If you have any questions or concerns, <Link href="https://broadlyhelp.force.com/s/contactsupport" external>send us a note</Link> and our Onboarding Specialist will make sure to address it during your scheduled call.
      </p>
      <div className="actions">
        <button onClick={setOnboardingStatusToComplete} className="button start-using-broadly-button">
          <span className="label">Start using Broadly</span>
          <ArrowRight/>
        </button>
      </div>
    </OnboardingSection>
  );
}
